/**
 * Web-side helpers for the `currency` element type — the labels, picker
 * options and entry-value readers the entry control, the card-definition
 * sub-editor, the filter builder and the conversion field share. Everything
 * that parses, normalises or formats money lives in the vendored shared
 * module (`./currency`); nothing here duplicates it (docs/plans/currency.md).
 */
import { CURRENCY_LIST, allowedCodes, defaultCodeOf, formatCurrency, getCurrency, isCurrencyValue, majorText, readCurrency, toMinor } from './currency';

/** "USD — US dollar"; the bare code for an unknown one. */
export const currencyLabel = (code) => {
	const currency = getCurrency(code);
	return currency ? `${currency.code} — ${currency.name}` : (code ? String(code) : '');
};

/** One antd Select option for an ISO code (null for an unknown code). */
export const currencyOption = (code) => {
	const currency = getCurrency(code);
	if (!currency) return null;
	return {
		value: currency.code,
		label: currencyLabel(currency.code),
		searchText: `${currency.code} ${currency.name} ${currency.symbol} ${currency.narrow}`,
	};
};

/**
 * Every currency the platform knows, as grouped Select options: the ordinary
 * currencies, then the nine ISO "fund" codes under their own heading — real
 * codes an element may allow, but not what anyone is looking for first.
 */
export const allCurrencyOptionGroups = () => [
	{ label: 'Currencies', options: CURRENCY_LIST.filter(c => !c.fund).map(c => currencyOption(c.code)) },
	{ label: 'Fund codes', options: CURRENCY_LIST.filter(c => c.fund).map(c => currencyOption(c.code)) },
];

/** Search filter for currency Selects: code, name and symbols. */
export const currencySearchFilter = (input, option) => {
	const needle = String(input || '').trim().toLowerCase();
	if (!needle) return true;
	return String(option?.searchText || option?.label || '').toLowerCase().includes(needle);
};

/** Above this many choices a currency Select offers a search box. */
export const CURRENCY_SEARCH_THRESHOLD = 5;

/**
 * The codes a person may ENTER for the element, in the author's order, plus
 * `storedCode` when a stored value is in a currency the element no longer
 * allows (tightening the list never hides a value on record, and re-editing
 * it must not silently change its currency).
 */
export const entryCurrencies = (options = {}, storedCode = null) => {
	const allowed = allowedCodes(options);
	if (storedCode && getCurrency(storedCode) && !allowed.includes(storedCode)) return [...allowed, storedCode];
	return allowed;
};

/** The currency a new entry opens in: the element's default, else its first. */
export const defaultCurrencyFor = (options = {}) => defaultCodeOf(options);

/**
 * What a stored value opens as in the entry control: `{ amount, currency }`
 * (the stored object, a legacy bare number in the default currency, a string
 * through the shared parser — readCurrency's tolerance) or null.
 */
export const readEntryCurrency = (raw, options = {}) => readCurrency(raw, options || {});

/**
 * The amount text an input shows for a stored value: exact, ungrouped major
 * units in its own currency ("1234.50", "1235" for JPY). '' when unreadable.
 */
export const amountTextOf = (value) => (isCurrencyValue(value) ? majorText(value.amount, value.currency) : '');

/**
 * Major-unit decimal text + a code -> the stored `{ amount, currency }`, on
 * the TEXT (19.99 -> 1999 exactly, never 19.99 * 100). null for blank text;
 * `{ error }` when the text is not an amount of that currency (too precise,
 * too large) — the caller decides how to say so.
 */
export const entryFromText = (text, code) => {
	const trimmed = typeof text === 'string' ? text.replace(/,/g, '').trim() : (typeof text === 'number' ? String(text) : '');
	if (!trimmed || trimmed === '-' || trimmed === '.') return null;
	const amount = toMinor(trimmed, code, 'refuse');
	if (amount === null) return { error: true, text: trimmed, currency: code };
	return { amount, currency: code };
};

/**
 * An amount's decimal text cut to `exponent` places, never rounded: what
 * the entry control lets through while someone types (a third decimal on a
 * USD amount is refused rather than rounded into a different number).
 */
export const clampDecimals = (text, exponent) => {
	if (typeof text !== 'string') return text;
	const cleaned = text.replace(/,/g, '');
	const dot = cleaned.indexOf('.');
	if (dot === -1) return cleaned;
	if (!exponent) return cleaned.slice(0, dot);
	return cleaned.slice(0, dot + 1 + exponent);
};

/** "1234.50" -> "1,234.50" (en-US grouping on the integer part only). */
export const groupAmountText = (text) => {
	if (typeof text !== 'string' || !text) return text;
	const negative = text.startsWith('-');
	const unsigned = negative ? text.slice(1) : text;
	const dot = unsigned.indexOf('.');
	const whole = dot === -1 ? unsigned : unsigned.slice(0, dot);
	const rest = dot === -1 ? '' : unsigned.slice(dot);
	return `${negative ? '-' : ''}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${rest}`;
};

/**
 * The KQL literal for an entry: always WITH its code and quoted — `"20.00
 * USD"` — because a bare number on a multi-currency element is refused (400
 * `currency_required`) and a one-currency element's list may grow later.
 * '' for anything that is not a readable entry.
 */
export const currencyLiteral = (entry) => {
	if (!isCurrencyValue(entry)) return '';
	const text = majorText(entry.amount, entry.currency);
	return text ? `${text} ${entry.currency}` : '';
};

/**
 * An entry as a person reads it on a chip or a condition row: the element's
 * own formatting ("$20.00", "CA$12.50"). '' when unreadable.
 */
export const formatEnteredCurrency = (entry, options = {}) => (
	isCurrencyValue(entry) ? formatCurrency(entry, options || {}) : ''
);
